import React, { useState, useEffect } from "react";
import DatePicker from "../../../shared/DatePicker";
import DropdownField from "../../../shared/DropdownField";
import { paymentMethods, getCardOptions } from "../../../constants/VisitsConstants";
import CustomButton from "../../../shared/CustomButton";
import moment from "moment";
import { useTranslation } from "react-i18next";

import CustomGeneralSearch from "./CustomGeneralSearch";
import TextInput from "../../../shared/TextInput";
import { setList } from "../../../redux/slices/AppSlice";
import { FaChartBar } from 'react-icons/fa';
import ExportInvoices from './ExportInvoices';
import { useDispatch, useSelector } from "react-redux";
import { fetchData } from "../../../helpers/fetchData";
import { omitBillingDatesForSkipInitialLoad, shouldSkipInitialBillingLoad } from "../../../utils/businessLevelAccess";
import toast from "react-hot-toast";

const BillingFilters = ({
  payloadData,
  setPayloadData,
  refetch,
  invoiceType,
  setSearchQuery,
  searchQuery,
  fetchInitialData,
  resetFilters,
  name,
  handleFetchZatcaStats,
  exportOptionsFormat,
  exportApiUrl,
  clearData,
  applyFilters
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchedValue, setSearchedValue] = useState(null);
  const { list } = useSelector((state) => state.app);
  const [departments, setDepartments] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [cardOptions, setCardOptions] = useState([]);
  const authUser = useSelector((state) => state?.auth?.user);
  const hideDateFilters = shouldSkipInitialBillingLoad(authUser);
  const isReceptionist =
    String(authUser?.role?.name_en || authUser?.role?.name || '').toLowerCase() === 'receptionist';

  const fetchPractitioners = async (departmentId) => {
    if (!departmentId) {
      setPractitioners([]);
      return;
    }

    try {
      const response = await fetchData('POST', `/v3/practitioner/getByDepartmentId?department_id=${departmentId}`, {});

      if (response?.practitioners) {
        setPractitioners(response.practitioners);
      } else {
        setPractitioners([]);
      }
    } catch (error) {
      console.error('Error fetching practitioners:', error);
      setPractitioners([]);
    }
  };

  useEffect(() => {
    fetchPractitioners(payloadData?.filters?.department_id);
  }, [payloadData?.filters?.department_id]);


  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await fetchData('POST', '/v3/department/getList', {});
        if (response?.data) {
          setDepartments(response.data);
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };
    fetchDepartments();
  }, []);

  useEffect(() => {
    const fetchReceptionists = async () => {
      if (isReceptionist) return;
      if (list?.receptionists?.length > 0) return;
      try {
        const response = await fetchData('GET', '/v3/invoices/getCreators', {});
        if (response?.data) {
          const _receptionists = response.data.map(receptionist => ({
            ...receptionist,
            label: receptionist?.staff_info?.user?.fullname
              ? `${receptionist?.staff_info?.user?.fullname} (${receptionist.created_by})`
              : receptionist.created_by,
            employee_id: receptionist.created_by,
          }));
          dispatch(setList({ key: 'receptionists', value: _receptionists }));
        }
      } catch (error) {
        console.error('Error fetching receptionists:', error);
      }
    };
    fetchReceptionists();
  }, [dispatch, isReceptionist, list?.receptionists]);

  useEffect(() => {
    if (payloadData?.filters?.payment_method === 'card') {
      getCardOptions(setCardOptions);
    }
  }, [payloadData?.filters?.payment_method]);

  const handleChange = (event) => {
    const { name, value } = event.target || {};
    setPayloadData((prevValues) => ({
      ...prevValues,
      [name]: moment(value).format("YYYY-MM-DD"),
    }));
  };

  const handleApplyFilters = () => {
    if (shouldSkipInitialBillingLoad(authUser)) {
      const values = [payloadData?.query, payloadData?.file_number, payloadData?.document_id, ...Object.values(payloadData?.filters || {})]
      if (!values.some(value => String(value ?? '').trim() !== '')) {
        toast.error(t('Please enter at least one filter'))
        return
      }
    }

    let payload = {
      ...payloadData,
      page: 1,
      per_page: payloadData?.per_page || 10,
    };

    let invoiceNumber = payload?.filters?.invoice_number;

    if (!invoiceNumber) {
      if (invoiceType === "cash") {
        invoiceNumber = payload?.filters?.cash_invoice_number;
      } else if (invoiceType === "insurance") {
        invoiceNumber = payload?.filters?.insurance_invoice_number;
      } else if (invoiceType === "corporate") {
        invoiceNumber = payload?.filters?.corporate_invoice_number;
      } else if (invoiceType === "government") {
        invoiceNumber = payload?.filters?.government_invoice_number;
      } else if (invoiceType === "creditCash") {
        invoiceNumber = payload?.filters?.credit_cash_invoice_number;
      } else if (invoiceType === "creditInsurance") {
        invoiceNumber = payload?.filters?.credit_insurance_invoice_number;
      } else if (invoiceType === "creditCorporate") {
        invoiceNumber = payload?.filters?.credit_corporate_invoice_number;
      } else if (invoiceType === "creditGovernment") {
        invoiceNumber = payload?.filters?.credit_government_invoice_number;
      } else if (invoiceType === "creditReceipt" || invoiceType === "creditPaid") {
        invoiceNumber = payload?.filters?.receipt_number || payload?.filters?.invoice_number;
      }
    }

    const {
      cash_invoice_number,
      insurance_invoice_number,
      corporate_invoice_number,
      government_invoice_number,
      credit_cash_invoice_number,
      credit_insurance_invoice_number,
      credit_corporate_invoice_number,
      credit_government_invoice_number,
      receipt_number,
      withdraw_invoice_number,
      deposit_invoice_number,
      ...normalizedFilters
    } = payload.filters || {};

    payload = {
      ...payload,
      filters: normalizedFilters,
    };

    let endpoint = "";
    let requestPayload = null;

    let apiPayload = {
      ...payload,
      filters: {
        ...payload.filters
      }
    };

    if (invoiceType === "cash") {
      apiPayload.filters.cash_invoice_number = invoiceNumber;
      delete apiPayload.filters.invoice_number;
      endpoint = "/v3/invoices/listPatient";
    } else if (invoiceType === "insurance") {
      apiPayload.filters.insurance_invoice_number = invoiceNumber;
      delete apiPayload.filters.invoice_number;
      endpoint = "/v3/invoices/listInsurance";
    } else if (invoiceType === "corporate") {
      apiPayload.filters.corporate_invoice_number = invoiceNumber;
      delete apiPayload.filters.invoice_number;
      endpoint = "/v3/invoices/listCooprate";
    } else if (invoiceType === "government") {
      apiPayload.filters.government_invoice_number = invoiceNumber;
      delete apiPayload.filters.invoice_number;
      endpoint = "/v3/invoices/listGovernment";
    } else if (invoiceType === "creditCash") {
      apiPayload.filters.credit_cash_invoice_number = invoiceNumber;
      delete apiPayload.filters.invoice_number;
      endpoint = "/v3/invoices/listCancelPatient";
    } else if (invoiceType === "creditInsurance") {
      apiPayload.filters.credit_insurance_invoice_number = invoiceNumber;
      delete apiPayload.filters.invoice_number;
      endpoint = "/v3/invoices/listCancelInsurance";
    } else if (invoiceType === "creditCorporate") {
      apiPayload.filters.credit_corporate_invoice_number = invoiceNumber;
      delete apiPayload.filters.invoice_number;
      endpoint = "/v3/invoices/listCancelCorporate";
    } else if (invoiceType === "creditGovernment") {
      apiPayload.filters.credit_government_invoice_number = invoiceNumber;
      delete apiPayload.filters.invoice_number;
      endpoint = "/v3/invoices/listCancelGovernment";
    } else if (invoiceType === "deposit") {
      apiPayload.filters.invoice_number = invoiceNumber;
      endpoint = "/v3/deposit/depositinvoices";
    } else if (invoiceType === "withdraw") {
      apiPayload.filters.invoice_number = invoiceNumber;
      endpoint = "/v3/deposit/getWithdrawDeposits";
    } else if (invoiceType === "creditReceipt" || invoiceType === "creditPaid") {
      requestPayload = {
        patient_name: payload.query || "",
        file_number: payload.file_number || payload.filters.file_number || "",
        document_id: payload.document_id || payload.filters.document_id || "",
        receipt_number: invoiceNumber || "",
        department_id: payload.filters.department_id || "",
        start_date: payload.start_date,
        end_date: payload.end_date,
        page: payload.page,
        per_page: payload.per_page
      };
      apiPayload.filters.receipt_number = invoiceNumber;
      delete apiPayload.filters.invoice_number;
      endpoint = invoiceType === "creditReceipt" ? "/v3/patient/creditBalance/listReceipt" : "/v3/patient/creditPayment/list";
    } else {
      apiPayload.filters.credit_insurance_invoice_number = invoiceNumber;
      delete apiPayload.filters.invoice_number;
      endpoint = "/v3/invoices/listCancelInsurance";
    }

    const filteredPayload = omitBillingDatesForSkipInitialLoad(authUser, payload);
    const filteredApiPayload = omitBillingDatesForSkipInitialLoad(authUser, apiPayload);
    const filteredRequestPayload = requestPayload
      ? omitBillingDatesForSkipInitialLoad(authUser, requestPayload)
      : null;

    if (applyFilters) {
      applyFilters(filteredPayload);
    } else {
      setPayloadData(filteredPayload);
      refetch("POST", endpoint, filteredRequestPayload || filteredApiPayload);
    }
  };

  const handleResetFilters = () => {
    if (resetFilters) {
      resetFilters({ omitDates: hideDateFilters });
      setSearchQuery("");
      setSearchedValue(null);
      if (hideDateFilters) clearData?.();
      return;
    }

    let payload = {
      document_id: "",
      start_date: moment().format("YYYY-MM-DD"),
      end_date: moment().format("YYYY-MM-DD"),
      filters: {
        file_number: "",
        created_by: "",
        payment_method: "",
        card_type: "",
        invoice_number: "",
        receipt_number: "",
        department_id: "",
        practitioner_id: "",
        document_id: "",
        episode_id: "",
      },
      query: "",
      page: 1,
      per_page: payloadData?.per_page || 10,
    };
    setSearchQuery("");
    setSearchedValue(null);
    setPractitioners([]); // Reset practitioners list
    payload = omitBillingDatesForSkipInitialLoad(authUser, payload);
    setPayloadData(payload);
    if (invoiceType === "cash") {
      refetch("POST", "/v3/invoices/listPatient", payload);
    } else if (invoiceType === "insurance") {
      refetch("POST", "/v3/invoices/listInsurance", payload);
    } else if (invoiceType === "corporate") {
      refetch("POST", "/v3/invoices/listCooprate", payload);
    } else if (invoiceType === "government") {
      refetch("POST", "/v3/invoices/listGovernment", payload);
    } else if (invoiceType === "creditCash") {
      refetch("POST", "/v3/invoices/listCancelPatient", payload);
    } else if (invoiceType === "creditInsurance") {
      refetch("POST", "/v3/invoices/listCancelInsurance", payload);
    } else if (invoiceType === "creditCorporate") {
      refetch("POST", "/v3/invoices/listCancelCorporate", payload);
    } else if (invoiceType === "creditGovernment") {
      refetch("POST", "/v3/invoices/listCancelGovernment", payload);
    } else if (invoiceType === "deposit") {
      refetch("POST", "/v3/deposit/depositinvoices", payload);
    } else if (invoiceType === "withdraw") {
      refetch("POST", "/v3/deposit/getWithdrawDeposits", payload);
    } else if (invoiceType === "creditReceipt" || invoiceType === "creditPaid") {
      const flatPayload = omitBillingDatesForSkipInitialLoad(authUser, {
        patient_name: "",
        file_number: "",
        document_id: "",
        receipt_number: "",
        department_id: "",
        start_date: moment().format("YYYY-MM-DD"),
        end_date: moment().format("YYYY-MM-DD"),
        page: 1,
        per_page: payload.per_page || 10
      });
      const endpoint = invoiceType === "creditReceipt" ? "/v3/patient/creditBalance/listReceipt" : "/v3/patient/creditPayment/list";
      refetch("POST", endpoint, flatPayload);
    } else {
      refetch("POST", "/v3/invoices/listCancelInsurance", payload);
    }
  };

  return (
    <div className="flex gap-3 mb-2 flex-wrap overflow-x-auto items-start">
      {!hideDateFilters && (
        <>
          <div className="flex-none w-[180px]">
            <DatePicker
              name="start_date"
              value={
                payloadData?.start_date
                  ? moment(payloadData?.start_date, "YYYY-MM-DD").toDate()
                  : ""
              }
              onChange={handleChange}
              useFormik={false}
              placeholder={t("StartDate")}
            />
          </div>

          <div className="flex-none w-[180px]">
            <DatePicker
              name="end_date"
              value={
                payloadData?.end_date
                  ? moment(payloadData?.end_date, "YYYY-MM-DD").toDate()
                  : ""
              }
              onChange={handleChange}
              useFormik={false}
              placeholder={t("EndDate")}
            />
          </div>
        </>
      )}

      <div className="flex-none w-[180px]">
        <CustomGeneralSearch
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          payload={payloadData}
          setPayload={setPayloadData}
          fetchInitialData={fetchInitialData}
        />
      </div>

      <div className="flex-none w-[180px]">
        <TextInput
          useFormik={false}
          name="invoice_number"
          value={payloadData?.filters?.invoice_number || ""}
          onChange={(e) => {
            setPayloadData((prevValues) => ({
              ...prevValues,
              filters: {
                ...prevValues?.filters,
                invoice_number: e.target.value,
              },
            }));
          }}
          placeholder={invoiceType === "creditReceipt" || invoiceType === "creditPaid" ? t("Receipt Number") : t("InvoiceNumber")}
        />
      </div>

      <div className="flex-none w-[180px]">
        <TextInput
          useFormik={false}
          name="file_number"
          value={payloadData?.filters?.file_number}
          onChange={(e) => {
            setPayloadData((prevValues) => ({
              ...prevValues,
              filters: {
                ...prevValues?.filters,
                file_number: e.target.value,
              },
            }));
          }}
          placeholder={t("MRN")}
        />
      </div>

      <div className="flex-none w-[180px]">
        <TextInput
          useFormik={false}
          name="document_id"
          value={
            invoiceType == "deposit" || invoiceType == "creditReceipt" || invoiceType == "creditPaid"
              ? payloadData?.document_id
              : payloadData?.filters?.document_id
          }
          onChange={(e) => {
            if (invoiceType == "deposit" || invoiceType == "creditReceipt" || invoiceType == "creditPaid") {
              setPayloadData((prevValues) => ({
                ...prevValues,
                document_id: e.target.value,
              }));
            } else {
              setPayloadData((prevValues) => ({
                ...prevValues,
                filters: {
                  ...prevValues?.filters,
                  document_id: e.target.value,
                },
              }));
            }
          }}
          placeholder={t("Document ID")}
        />
      </div>

      <div className="flex-none w-[180px]">
        <TextInput
          useFormik={false}
          name="episode_id"
          value={payloadData?.filters?.episode_id || ""}
          onChange={(e) => {
            setPayloadData((prevValues) => ({
              ...prevValues,
              filters: {
                ...prevValues?.filters,
                episode_id: e.target.value,
              },
            }));
          }}
          placeholder={t("Episode ID")}
        />
      </div>

      <div className="flex-none w-[180px]">
        <DropdownField
          name="department_id"
          value={payloadData?.filters?.department_id}
          useFormik={false}
          placeholder={t("Select Department")}
          options={departments?.map((item) => ({
            label: item?.name_en,
            value: item?.id,
          }))}
          filter
          onChange={(event) => {
            let payload = {
              ...payloadData,
              filters: {
                ...payloadData?.filters,
                department_id: event?.target?.value,
                practitioner_id: '' // Reset practitioner when department changes
              },
            };
            setPayloadData(payload);
          }}
        />
      </div>

      <div className="flex-none w-[180px]">
        <DropdownField
          name="practitioner_id"
          value={payloadData?.filters?.practitioner_id}
          useFormik={false}
          placeholder={t("Select Practitioner")}
          options={practitioners?.map((item) => ({
            label: item?.name,
            value: item?.practitioner_id,
          }))}
          filter
          onChange={(event) => {
            let payload = {
              ...payloadData,
              filters: {
                ...payloadData?.filters,
                practitioner_id: event?.target?.value,
              },
            };
            setPayloadData(payload);
          }}
        />
      </div>

      {!isReceptionist && invoiceType !== "deposit" && invoiceType !== "withdraw" && invoiceType !== "creditReceipt" && invoiceType !== "creditPaid" && (
        <div className="flex-none w-[180px]">
          <DropdownField
            name="created_by"
            value={payloadData?.filters?.created_by}
            useFormik={false}
            placeholder={t("Select Receptionist")}
            options={list?.receptionists}
            optionLabel="label"
            optionValue="employee_id"
            filter
            onChange={(event) => {
              let payload = {
                ...payloadData,
                filters: {
                  ...payloadData?.filters,
                  created_by: event?.target?.value,
                },
              };
              setPayloadData(payload);
            }}
          />
        </div>
      )}

      {invoiceType !== "deposit" && invoiceType !== "withdraw" && invoiceType !== "creditReceipt" && invoiceType !== "creditPaid" && (
        <div className="flex-none w-[180px]">
          <DropdownField
            name="payment_method"
            value={payloadData?.filters?.payment_method}
            useFormik={false}
            placeholder={t("Select Payment Method")}
            options={paymentMethods(t)}
            onChange={(event) => {
              let payload = {
                ...payloadData,
                filters: {
                  ...payloadData?.filters,
                  payment_method: event?.target?.value,
                  card_type: event?.target?.value === 'card' ? payloadData?.filters?.card_type : '',
                },
              };
              setPayloadData(payload);
            }}
          />
        </div>
      )}

      {invoiceType !== "deposit" && invoiceType !== "withdraw" && invoiceType !== "creditReceipt" && invoiceType !== "creditPaid" && payloadData?.filters?.payment_method === 'card' && (
        <div className="flex-none w-[180px]">
          <DropdownField
            name="card_type"
            value={payloadData?.filters?.card_type}
            useFormik={false}
            placeholder={t("Select Card Type")}
            options={cardOptions}
            filter
            onChange={(event) => {
              let payload = {
                ...payloadData,
                filters: {
                  ...payloadData?.filters,
                  card_type: event?.target?.value,
                },
              };
              setPayloadData(payload);
            }}
          />
        </div>
      )}

      <div className="flex-none flex gap-2 items-center">
        <CustomButton
          //label={t("ApplyFilters")}
          title={t("ApplyFilters")}
          buttonType="primary"
          icon={"pi pi-filter"}
          className="w-fit h-[35px]"
          onClick={handleApplyFilters}
        />
        <CustomButton
            //label={t("Reset")}
            title={t("Reset")}
            buttonType="reset"
            icon={"pi pi-sync"}
            className="w-fit h-[35px]"
            onClick={handleResetFilters}
          />
        {
          name && exportApiUrl && exportOptionsFormat && (
            <ExportInvoices
              payload={{
                ...(!hideDateFilters && {
                  start_date: payloadData?.start_date,
                  end_date: payloadData?.end_date,
                }),
                query: payloadData?.query,
                lang: 'en'
              }}
              apiUrl={exportApiUrl}
              exportOptionsFormat={exportOptionsFormat}
              name={name}
            />
          )
        }
        {
          handleFetchZatcaStats && (
            <CustomButton
              icon={<FaChartBar size={15} />}
              //label={t('ZATCA Stats')}
              title={t('ZATCA Stats')}
              buttonType="success"
              onClick={handleFetchZatcaStats}
              className="!w-fit"
            />
          )
        }

      </div>
    </div>
  );
};

export default BillingFilters;
